/*!
 * teardown.js — what "stop everything" stops, kept by the modules that start it
 * © 2026 Rob Canning — GPLv3
 *
 * Each module that leaves something running (a sound, a timer, a loop, a
 * listener, a registry of the score's elements) registers its own stop here
 * once, at import, under one of three kinds:
 *
 *   sound    what hush() silences: voices, impulses, pending starts, faders
 *   stage    what still(all) clears: motion, texts, strobes, the screen's
 *            filters, masks, trails
 *   project  what belongs to the score that was open: per-uid maps, caches,
 *            overlays, pads, launchers, watchers, extensions
 *
 * The callers run kinds, not lists: hush() runs "sound", still(all) "stage",
 * Oscilla Live's next example (clearStage) "sound" + "stage", and a project
 * switch (cleanupProjectOverlays) all three. A page loads only the modules it
 * uses, so it only stops what it could have started: Live never registers
 * the score app's parts, and a lazily loaded cue registers when it first runs.
 *
 * ownerSignal(owner, key) is the same idea for one thing re-made: a handler
 * run again (a console re-run, a DSL-editor save, a rescan, an editor opened
 * again) asks for a fresh signal, which aborts the one it handed out last
 * time, so listeners added with { signal } and timers tied to it go with the
 * old run. A "project" reset aborts every signal still live.
 *
 * A leaf: no imports, so Live, the score app and node tests load it alone.
 */

const KINDS = ["sound", "stage", "project"];
const resets = { sound: [], stage: [], project: [] };

/**
 * Register `fn` to run on every reset of `kind`. `name` shows in a failure's
 * warning and in resetsOf() (a probe can list what registered).
 */
export function onReset(kind, fn, name = fn.name || "anonymous") {
  if (!resets[kind]) throw new Error(`teardown: unknown kind "${kind}" (${KINDS.join(", ")})`);
  resets[kind].push({ fn, name });
}

/** Run every registered stop of each kind, in that order; one that throws does not stop the rest. */
export function runReset(...kinds) {
  for (const kind of kinds) {
    for (const { fn, name } of resets[kind] ?? []) {
      try { fn(); } catch (err) { console.warn(`[teardown] ${kind}: ${name} failed:`, err); }
    }
    if (kind === "project") abortEveryOwner();
  }
}

/** The names registered under `kind`, in the order they run. */
export const resetsOf = kind => (resets[kind] ?? []).map(r => r.name);

// owner (an element, an object or a string such as a uid) -> key -> AbortController
const owners = new Map();

/**
 * A fresh AbortSignal for `owner` + `key`, aborting the one handed out last
 * for them. Pass it to addEventListener({ signal }), and tie timers to it
 * with signal.addEventListener("abort", ...). The owner is what the run is
 * for: the uid when a re-run makes a new element, the element when it stays.
 */
export function ownerSignal(owner, key = "") {
  releaseOwner(owner, key);
  const ctrl = new AbortController();
  let keys = owners.get(owner);
  if (!keys) owners.set(owner, keys = new Map());
  keys.set(key, ctrl);
  return ctrl.signal;
}

/** Abort what `owner` + `key` holds (every key of the owner when `key` is left out). */
export function releaseOwner(owner, key) {
  const keys = owners.get(owner);
  if (!keys) return;
  for (const [k, ctrl] of [...keys]) {
    if (key !== undefined && k !== key) continue;
    keys.delete(k);
    ctrl.abort();
  }
  if (!keys.size) owners.delete(owner);
}

/** How many signals are live (for probes and tests). */
export const liveOwnerSignals = () => [...owners.values()].reduce((n, keys) => n + keys.size, 0);

function abortEveryOwner() {
  const all = [...owners.values()].flatMap(keys => [...keys.values()]);
  owners.clear();
  for (const ctrl of all) ctrl.abort();
}
